/**
 * Current User — backend-truthful role + permissions.
 *
 * Single frontend source of authorization truth.
 * Fetches from GET /api/me which reads from platform DB RBAC.
 * Auth0 is identity-only — this hook provides authorization context.
 */

import { useEffect, useState } from "react"
import { useAuthAccess } from "@/lib/auth"
import { getAdapter } from "@/lib/api/adapter"
import type { MeResponse } from "@/lib/api/adapter/IDataAdapter"

interface CurrentUserState {
  loaded: boolean
  role: string | null
  permissions: string[]
}

let cachedMe: MeResponse | null = null

export function useCurrentUser(): CurrentUserState & {
  isAdmin: boolean
  hasPermission: (code: string) => boolean
} {
  const { isAuthenticated, isLoading } = useAuthAccess()
  const [state, setState] = useState<CurrentUserState>({
    loaded: cachedMe !== null,
    role: cachedMe?.role ?? null,
    permissions: cachedMe?.permissions ?? [],
  })

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      if (!isLoading && !isAuthenticated) {
        setState({ loaded: true, role: null, permissions: [] })
        cachedMe = null
      }
      return
    }
    if (cachedMe) return // already fetched this session

    let cancelled = false
    getAdapter()
      .getMe()
      .then((me) => {
        if (!cancelled) {
          cachedMe = me
          setState({
            loaded: true,
            role: me.role,
            permissions: me.permissions,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ loaded: true, role: null, permissions: [] })
        }
      })

    return () => { cancelled = true }
  }, [isAuthenticated, isLoading])

  const isAdmin = state.role === "Admin User"
  const hasPermission = (code: string) => state.permissions.includes(code)

  return { ...state, isAdmin, hasPermission }
}
